import api from '../services/api'
import { masterclassService } from '../services/masterclassService'
import {
  academy,
  admissions,
  facilities,
  mentors,
  masterclass,
  faq,
  pathways,
  leadQualificationShape,
} from './smaKnowledge'
import {
  applyLiveCourses,
  getMergedCourses,
  findCourse,
  coursesByCategory,
  coursesByInterest,
  matchCourseFromQuery,
  courseFieldCoverage,
} from './courseKnowledge'

/**
 * Last-resort advisor copy. Mirrors confirmed academy knowledge / backend advisorFacts.
 * Live fees and durations come from PostgreSQL.
 * Educational course detail comes from published course knowledge on the backend.
 * Intake dates are never hardcoded here.
 */
const CONFIRMED = {
  slogan: 'Shaping Futures',
  email: 'synergymultimediaacademy@gmail.com',
  website: 'www.synergymultimediaacademy.com',
  phone: '+256 706 739 311',
  location: 'Makerere Kubiri, KK Building, alongside Gayaza Road',
  hoursVisit: 'SMA is open for visits on weekdays from 9:00 AM to 5:00 PM.',
  founded: 'Synergy Multimedia Academy was founded in 2025.',
  registrationFee: '50,000 UGX',
  durationPolicy: 'Exact course duration depends on the program. I can check the live duration for a specific SMA course.',
  installments: 'Course fees can be paid in installments, with 50% paid before starting and the remaining balance paid during the course.',
  paymentPlace: 'You can pay at the academy or via Mobile Money on 0763875564.',
  certificate: 'Students receive a certificate upon completion.',
  facilities: 'SMA has the equipment needed for practical training.',
  equipmentAdvantage: 'Having your own equipment is an added advantage, and it is not mandatory unless a specific course says so.',
  photoEquipment: 'For photography, that can include cameras, lenses, lights, and modifiers.',
  requirements: 'General SMA admission has no blanket academic qualification requirement. Specific courses may have their own recommended prerequisites.',
  intakeUnknown: "I don't have confirmed current intake dates, so I won't guess. You can reach SMA Admissions through the contact details here.",
  studyTimes: 'Classes generally run at 9:00 AM, 12:00 PM, and 6:00 PM.',
}

let upcomingMasterclass = null
let liveHydrated = false

function tokenize(query) {
  return String(query || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s+]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 2)
}

function scoreMatch(haystack, tokens) {
  const text = haystack.toLowerCase()
  return tokens.reduce((score, token) => score + (text.includes(token) ? 1 : 0), 0)
}

export function getAcademy() {
  return academy
}

export function getAdmissions() {
  return admissions
}

export function getFacilities() {
  return facilities
}

export function getMentors() {
  return mentors
}

export function getMasterclassFacts() {
  return masterclass
}

export function getCachedUpcomingMasterclass() {
  return upcomingMasterclass
}

export function getFaq() {
  return faq
}

export function getPathways() {
  return pathways
}

export function getCourses() {
  return getMergedCourses()
}

export function getCourse(code) {
  return findCourse(code)
}

export function getCoursesByCategory(category) {
  return coursesByCategory(category)
}

export function getCoursesByInterest(interestId) {
  return coursesByInterest(interestId)
}

export function getRelatedCourses(code) {
  const course = findCourse(code)
  if (!course) return []
  return course.relatedCourseCodes.map(findCourse).filter(Boolean)
}

export function getLeadQualificationShape() {
  return { ...leadQualificationShape }
}

export function getCoverage() {
  return courseFieldCoverage
}

export function getOfficialEmail() {
  return CONFIRMED.email
}

export function getOfficialWebsite() {
  return CONFIRMED.website
}

export function getOfficialSlogan() {
  return CONFIRMED.slogan
}

export function getFoundingYearAdvisorLines() {
  return [CONFIRMED.founded]
}

export function getHoursAdvisorLines() {
  return [CONFIRMED.hoursVisit]
}

export function getDurationAdvisorLines() {
  return [CONFIRMED.durationPolicy]
}

export function getIntakeAdvisorLines() {
  return [
    CONFIRMED.intakeUnknown,
    CONFIRMED.studyTimes,
    CONFIRMED.durationPolicy,
    `${CONFIRMED.installments} ${CONFIRMED.paymentPlace} There is also a ${CONFIRMED.registrationFee} registration fee.`,
  ]
}

export function getFacilitiesAdvisorLines() {
  return [CONFIRMED.facilities, CONFIRMED.equipmentAdvantage]
}

export function getLocationAdvisorLines() {
  return [
    `SMA is at ${CONFIRMED.location}.`,
    'Visiting hours are weekdays, 9:00 AM to 5:00 PM.',
  ]
}

export function getPaymentAdvisorLines() {
  return [
    CONFIRMED.installments,
    CONFIRMED.paymentPlace,
    `There is also a ${CONFIRMED.registrationFee} registration fee.`,
  ]
}

export function getCertificateAdvisorLines() {
  return [CONFIRMED.certificate]
}

export function getEquipmentAdvisorLines() {
  return [
    CONFIRMED.facilities,
    CONFIRMED.equipmentAdvantage,
    CONFIRMED.photoEquipment,
  ]
}

export function getVisitAdvisorLines() {
  return [
    `Yes. You are welcome to visit SMA at ${CONFIRMED.location}.`,
    'Visiting hours are weekdays, 9:00 AM to 5:00 PM.',
    'You can reach SMA Admissions through the contact details here.',
  ]
}

export function getRequirementsAdvisorLines() {
  return [CONFIRMED.requirements]
}

export function getMasterclassAdvisorLines() {
  const session = upcomingMasterclass
  const when = formatMasterclassDate(session)
  if (when) {
    const topic = session.topic ? ` “${session.topic}”` : ''
    return [`The next SMA Masterclass is${topic} on ${when}.`]
  }
  return [
    'SMA hosts a monthly Masterclass series with industry practitioners.',
    'I don’t have a confirmed upcoming date yet, so I won’t guess. You can check the Masterclass page, or I can connect you with Admissions.',
  ]
}

const TOOL_ALIASES = [
  ['premiere pro', 'Premiere Pro'],
  ['premiere', 'Premiere Pro'],
  ['photoshop', 'Photoshop'],
  ['lightroom', 'Lightroom'],
  ['illustrator', 'Illustrator'],
  ['figma', 'Figma'],
  ['after effects', 'After Effects'],
  ['davinci', 'DaVinci Resolve'],
  ['midjourney', 'Midjourney'],
  ['chatgpt', 'ChatGPT'],
  ['firefly', 'Adobe Firefly'],
]

// Last-resort marketing-catalog match. Educational tool lists come from published course knowledge on the backend.
export function findToolAdvisorLines(query) {
  const lower = String(query || '').toLowerCase()
  const alias = TOOL_ALIASES.find(([needle]) => lower.includes(needle))
  if (!alias) return null

  const matches = getMergedCourses().filter((course) => {
    const hay = `${(course.software?.items || []).join(' ')} ${course.description || ''} ${(course.advantages || []).join(' ')}`.toLowerCase()
    return hay.includes(alias[0])
  })

  if (!matches.length) return null
  const titles = matches.slice(0, 3).map((course) => course.title).join(', ')
  return [`Yes. ${alias[1]} is part of training in ${titles}.`]
}

export function getHumanAdvisorLines() {
  return [
    'You can reach SMA Admissions through the contact details here.',
  ]
}

export function getUnknownAdvisorLines() {
  return [
    "I don't have confirmed information on that yet, so I won't guess.",
    `An SMA admissions advisor can help — call ${CONFIRMED.phone} or email ${CONFIRMED.email}.`,
  ]
}

export function getCourseFeeLine(course) {
  if (!course) return null
  if (course.feeSource === 'postgresql' && course.fee) {
    const duration = course.durationSource === 'postgresql' && course.duration
      ? ` — ${course.duration}`
      : ''
    return `${course.name} — ${course.fee}${duration}`
  }
  return `${course.name} — I don't have a confirmed live fee for this program yet.`
}

export function formatMasterclassDate(session) {
  if (!session?.event_date) return null
  const raw = String(session.event_date)
  const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  const date = dateOnly
    ? new Date(`${dateOnly[1]}T12:00:00+03:00`)
    : new Date(session.event_date)
  if (Number.isNaN(date.getTime())) return null
  const day = date.toLocaleDateString('en-UG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Kampala',
  })
  const time = session.start_time ? ` at ${session.start_time}` : ''
  return `${day}${time}`
}

export async function hydrateAdvisorLiveData() {
  const tasks = [
    api.get('/auth/courses')
      .then((response) => applyLiveCourses(response.data || []))
      .catch(() => applyLiveCourses([])),
    masterclassService.getUpcoming()
      .then((data) => {
        upcomingMasterclass = data?.masterclass || null
      })
      .catch(() => {
        upcomingMasterclass = null
      }),
  ]

  await Promise.all(tasks)
  liveHydrated = true
  return { courses: getMergedCourses(), upcomingMasterclass, liveHydrated }
}

export async function getUpcomingMasterclass() {
  if (liveHydrated) return upcomingMasterclass
  try {
    const data = await masterclassService.getUpcoming()
    upcomingMasterclass = data?.masterclass || null
  } catch {
    upcomingMasterclass = null
  }
  return upcomingMasterclass
}

export function findCourseForQuestion(query) {
  return matchCourseFromQuery(query)
}

export function searchKnowledge(query) {
  const tokens = tokenize(query)
  if (!tokens.length) {
    return { courses: [], faq: [], mentors: [], admissions: [], facilities: [] }
  }

  const courses = getMergedCourses()
    .map((course) => ({
      item: course,
      score: scoreMatch(`${course.name} ${course.category} ${course.description} ${(course.learningOutcomes || []).join(' ')}`, tokens),
    }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((row) => row.item)

  return {
    courses,
    faq: [],
    mentors: [],
    admissions: [],
    facilities: [],
  }
}

export const smaKnowledge = {
  academy,
  admissions,
  facilities,
  mentors,
  masterclass,
  faq,
  pathways,
  get courses() {
    return getMergedCourses()
  },
}
